import { Injectable } from '@angular/core';
import { DataService } from '../Data/data.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DatabindingService {
  static ALLOWED_TYPES_OF_DOCUMENT_FIELDS = ['text', 'checkbox', 'radiobutton', 'enumeration'];
  currentRecordType = '';
  objectMetadataList = [];

  constructor(private dataService: DataService) {}

  /**
   * the function of creating or preparing a new field based on an existing field from an existing document
   *
   * @param selectedDocument - target document from which we want copy the field
   * @param currentField - field based on which we want to create a new one
   */
  initDefaultFieldForPushback(selectedDocument: any, currentField: any): any {
    // notice: this is mutable function
    const setFieldPropertyIfValueExist = (_field, fieldPropertyName, valueToSet) =>
      valueToSet ? (_field[fieldPropertyName] = valueToSet) : null;

    // Maybe we should copy all field data from template and then additionally extract json_attributes to this object?
    const field: any = {
      label: currentField.json_attributes.label,
      prefilled_text: currentField.json_attributes.prefilled_text,
      x: currentField.json_attributes.x,
      y: currentField.json_attributes.y,
      width: currentField.json_attributes.width,
      height: currentField.json_attributes.height,
      page_number: currentField.json_attributes.page_number,
      required: currentField.json_attributes.required,
      type: currentField.type,
      role: currentField.role,
      integrations: []
    };

    // fonts properties from signNow Editor "Formatting" block for text fields
    setFieldPropertyIfValueExist(field, 'size', currentField.json_attributes.size);
    setFieldPropertyIfValueExist(field, 'bold', currentField.json_attributes.bold);
    setFieldPropertyIfValueExist(field, 'italic', currentField.json_attributes.italic);
    setFieldPropertyIfValueExist(field, 'underline', currentField.json_attributes.underline);
    setFieldPropertyIfValueExist(field, 'align', currentField.json_attributes.align);
    setFieldPropertyIfValueExist(field, 'color', currentField.json_attributes.color);

    setFieldPropertyIfValueExist(field, 'role_color', currentField.role_color);
    setFieldPropertyIfValueExist(field, 'lock_to_sign_date', currentField.json_attributes.lock_to_sign_date);
    setFieldPropertyIfValueExist(field, 'arrangement', currentField.json_attributes.arrangement);
    setFieldPropertyIfValueExist(field, 'font', currentField.json_attributes.font);
    setFieldPropertyIfValueExist(field, 'max_chars', currentField.json_attributes.max_chars);
    setFieldPropertyIfValueExist(field, 'max_lines', currentField.json_attributes.max_lines);
    setFieldPropertyIfValueExist(field, 'valign', currentField.json_attributes.valign);
    setFieldPropertyIfValueExist(field, 'name', currentField.json_attributes.name);
    setFieldPropertyIfValueExist(field, 'validator_id', currentField.json_attributes.validator_id);

    // for conditional
    setFieldPropertyIfValueExist(field, 'dependency', currentField.json_attributes.dependency);

    // for calculated fields
    if (currentField.type === 'calculated' || currentField.json_attributes.calculation_formula) {
      field.calculation_precision = currentField.json_attributes.calculation_precision;
      field.calculation_formula = currentField.json_attributes.calculation_formula;
      field.formula = currentField.json_attributes.formula;
    }

    // TODO should we use "is_editable" property?

    // if ( currentField.type === 'checkbox') { }

    if (currentField.type === 'radiobutton') {
      field.radio = currentField.radio;
      const isIntegrationArrayExist = currentField.integrations && currentField.integrations.length;

      if (isIntegrationArrayExist && currentField.integrations[0].isAppendToSN === 'yes') {
        field.radio.forEach((radioItem) => {
          if (radioItem.value === field.prefilled_text) {
            radioItem.checked = '1';
          }
        });
      }
    }

    if (currentField.type === 'enumeration') {
      field.enumeration_options = [];
      const isEnumerationOptionsExist =
        selectedDocument.enumeration_options && selectedDocument.enumeration_options.length;

      if (isEnumerationOptionsExist) {
        for (let idx = 0; idx < selectedDocument.enumeration_options.length; idx++) {
          if (selectedDocument.enumeration_options[idx].enumeration_id === currentField.id) {
            field.enumeration_options.push(selectedDocument.enumeration_options[idx].data);
          }
        }
      }
    }

    return field;
  }

  /**
   * the function of preparing the routing object with template_data array
   * loop through document roles and setup routing details
   *
   * @param document - target document from which we want take data for preparing routing object
   */
  createRoutingDetailsObject(document: any) {
    const routing = {
      template_data: []
    };

    for (let i = 0; i < document.roles.length; i++) {
      const rout: any = {
        name: document.roles[i].name,
        role_id: document.roles[i].unique_id,
        inviter_role: false,
        default_email: '',
        signing_order: document.roles[i].signing_order
      };

      if (document.roles[i].payment_request) {
        rout.payment_request = document.roles[i].payment_request;
      }

      routing.template_data.push(rout);
    }

    return routing;
  }

  /**
   * the function of copying a property from one object to another if the property exists
   *
   * @param refToDataObject - object that contains the information that we want to copy
   * @param refToCopyingObject - the object to which we want to copy the property
   * @param propertyName - property of refToDataObject what we wan to copy
   */
  copyingPropertyIfExistToAnotherObject(refToDataObject, refToCopyingObject, propertyName): void {
    if (refToDataObject[propertyName]) {
      refToCopyingObject[propertyName] = refToDataObject[propertyName];
    }
  }

  /**
   * the function for getting metadata from salesforce object
   *
   * @param objectType - name of SF object
   */
  getSalesforceObjectMetadata(objectType: string): Observable<any> {
    return new Observable((observer) => {
      if (
        this.currentRecordType.toLowerCase() === objectType.toLowerCase() &&
        this.objectMetadataList.length
      ) {
        if (this.objectMetadataList.length) {
          observer.next(this.objectMetadataList);
        }
      } else {
        this.currentRecordType = objectType.toLowerCase();
        this.objectMetadataList = [];

        // setup params for getting SF Object Metadata from API
        const salesforce_data = this.dataService.getSalesforceData();
        const params = {
          sf_token: salesforce_data.sf_token,
          instance_url: salesforce_data.instance_url,
          sf_obg_type: objectType
        };

        this.dataService.apiGetCall('/api/get_sf_object_meta', { params }).subscribe(
          (meta: any) => {
            if (meta && meta.fields) {
              const fields = meta.fields;

              // sort metadata object fields alphabetically by name
              fields.sort((a, b) => {
                const nameA = a.name.toLowerCase();
                const nameB = b.name.toLowerCase();

                if (nameA < nameB) {
                  return -1;
                }
                if (nameA > nameB) {
                  return +1;
                }

                return 0;
              });

              for (let idx = 0; idx < fields.length; ++idx) {
                // we should choose only suitable types of fields
                if (
                  fields[idx].updateable &&
                  // !fields[idx].custom &&
                  fields[idx].soapType !== 'tns:ID' &&
                  // fields[idx].soapType !== 'xsd:boolean' &&
                  fields[idx].relationshipName === null &&
                  fields[idx].type !== 'reference' &&
                  // fields[idx].type !== 'date' &&     // now we show this type but not use "update" checkbox if it selected for field
                  // fields[idx].type !== 'datetime' && // now we show this type but not use "update" checkbox if it selected for field
                  // fields[idx].type !== 'picklist' &&
                  fields[idx].type !== 'multipicklist' &&
                  fields[idx].type !== 'id'
                ) {
                  this.objectMetadataList.push({
                    name: fields[idx].name,
                    label: fields[idx].label,
                    type: fields[idx].type,
                    soapType: fields[idx].soapType
                  });
                }
              }

              observer.next(this.objectMetadataList);
            } else if (meta && meta.errorCode) {
              console.log(meta.errorCode);
            }
          },
          (error) => observer.error(error),
          () => observer.complete()
        );
      }
    });
  }

  /**
   * check if this type of SF field is available for update from our side on current time
   *
   * @param sfObjectFieldName - name of SF field name (API name)
   * @param sfObjectMetadataList - array with all fields metadata from SF Object (this fields objects should consist at least name and type properties)
   */
  isFieldNotAvailabelForUpdate(sfObjectFieldName, sfObjectMetadataList): boolean {
    return sfObjectMetadataList.some(
      (item) => item.name === sfObjectFieldName && (item.type === 'date' || item.type === 'datetime')
    );
  }

  isAllowedTypeOfDocumentField(fieldType: string): boolean {
    return DatabindingService.ALLOWED_TYPES_OF_DOCUMENT_FIELDS.includes(fieldType);
  }
}
